import { Alert, Button, Checkbox, Form, Input } from 'antd'
import { useEffect, useState } from "react";
import { UserOutlined, LockOutlined, CopyrightCircleOutlined } from '@ant-design/icons'
import './login.less'
import { NavLink, useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import localStore, { REMEMBER_USERNAME, REMEMBER_TAG, REMEMBER_PASSWORD, USER_UUID, AUTHORIZATION } from '@/store/local-store';
import { userCheckLogin, userLogin } from '@/declare/api/project/user';
import { User } from '@/declare/interface/project/user';
/**
 * `Login` 组件
 * 
 * 功能描述：
 * - 提供用户登录功能，包括输入用户名、密码，以及是否记住登录信息
 * - 使用加密工具 `CryptoJS` 对密码进行加密
 * - 使用 `localStore` 管理本地存储的用户信息和登录状态
 */

export default function Login() {
    /**
     * 表单实例
     * - 用于管理表单数据和状态
     */
    const [form] = Form.useForm();

    /**
     * 错误信息
     * - 保存登录失败时的错误提示信息
     */
    const [errorMessage, setErrorMessage] = useState('');

    /**
     * 加载状态
     * - 控制登录按钮是否处于加载状态
     */
    const [loading, setLoading] = useState(false);

    /**
     * 路由跳转方法
     * - 用于跳转页面，如登录成功后跳转到桌面页面
     */
    const navigate = useNavigate();

    /**
     * 组件刷新触发器
     * - 通过改变布尔值，强制组件重新渲染
     */
    const [refresh, setRefresh] = useState<boolean>(false);

    /**
     * 自动填充账户信息
     * - 从本地存储获取保存的用户名、密码和"记住我"状态。
     * - 解密 Base64 编码的密码并填充到表单中。
     * - 在组件加载时触发。
     */
    useEffect(() => {
        // 从本地存储中获取自动保存的账户信息
        let username = localStore.get(REMEMBER_USERNAME);
        let remember = localStore.get(REMEMBER_TAG);
        let password = localStore.get(REMEMBER_PASSWORD) || '';
        form.setFieldsValue({
            username: username,
            remember: remember,
            password: CryptoJS.enc.Base64.parse(password).toString(CryptoJS.enc.Utf8),
        });
    }, [])
    /**
     * 检查登录状态
     * - 调用登录检查 API。
     * - 如果未登录，清除本地存储并停留在登录页面。
     * - 如果已登录，跳转到桌面页面。
     */
    useEffect(() => {
        // checkout login
        if (localStore.token == null) {
            localStore.clear();
            return;
        }
        checkLogin();
    }, [])
    /**
     * 检查用户是否已登录
     * - 调用后端登录检查接口 `userCheckLogin`。
     * - 登录成功后，将用户信息保存到本地存储，并跳转到桌面页面。
     * - 登录失败则清除本地存储。
     */
    const checkLogin = () => {
        userCheckLogin()
            .then(res => {
                let userDetail: User = res.data;
                localStore.set(USER_UUID, userDetail.id);
                localStore.userUuid = userDetail.uuid;
                navigate('/desktop');
            }).catch(err => {
                localStore.clear();
            })
    }

    /**
     * 用户登录方法
     * - 收集表单中的用户名和密码，进行加密处理后发送到后端 API。
     * - 登录成功后，保存用户的 Token 和其他信息到本地存储。
     * - 如果选择 "记住我"，保存用户名和加密后的密码。
     * - 登录失败则显示错误信息。
     * 
     * @param values 表单输入的用户名和密码
     */
    const login = (values) => {
        //错误信息清空
        setErrorMessage('');
        setLoading(true);

        let data: User = {
            username: values.username,
            password: CryptoJS.MD5(values.password).toString()
        }
        userLogin(data).then(res => {
            let userDetail: User = res.data;
            // 登录失败
            if (res.status !== 200) {
                setErrorMessage(res?.message);
                setRefresh(!refresh);
                return;
            }
            localStore.set(AUTHORIZATION, userDetail.token);
            localStore.set(USER_UUID, userDetail.id);
            localStore.userUuid = userDetail.uuid;
            if (values.remember) {
                localStore.set(REMEMBER_USERNAME, values.username);
                localStore.set(REMEMBER_PASSWORD, CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(values.password)));
                localStore.set(REMEMBER_TAG, values.remember);
            } else {
                localStore.remove(REMEMBER_USERNAME);
                localStore.remove(REMEMBER_PASSWORD);
                localStore.remove(REMEMBER_TAG);
            }
            navigate('/desktop');
        }).catch(err => {
            console.log(err);
            setErrorMessage('登录失败')
            setRefresh(!refresh);
        }).finally(() => {
            setLoading(false);
        })

    }

    return (
        <div className="login-container">
            <div className="title">
                NetBuilding
            </div>
            <div className="content">
                <Form className="login-form" form={form} onFinish={login} autoComplete="off">
                    {!!errorMessage && (
                        <Alert
                            style={{ marginBottom: '10px' }}
                            message={errorMessage}
                            type="error"
                            closable
                            onClose={() => setErrorMessage('')} />
                    )}

                    <Form.Item name="username" rules={[{ required: true, message: '用户名不能为空' }]}>
                        <Input prefix={<UserOutlined />} placeholder="用户名" />
                    </Form.Item>
                    <Form.Item name="password" rules={[{ required: true, message: '密码不能为空' }]}>
                        <Input.Password prefix={<LockOutlined />} placeholder="密码" />
                    </Form.Item>
                    <Form.Item>
                        <Form.Item name="remember" valuePropName="checked" noStyle>
                            <Checkbox>记住我</Checkbox>
                        </Form.Item>
                        <NavLink className="login-form-forgot" to={"/forget"}>
                            忘记密码?
                        </NavLink>
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" className="login-form-button" loading={loading}>
                            登 录
                        </Button>

                    </Form.Item>
                </Form>
            </div>
            <div className="footer">
                <div className="copyright">
                    Copyright <CopyrightCircleOutlined /> 2024
                    <a href="http://www.tncet.com" target="_blank">同恩工程技术</a>
                </div>
            </div>
        </div>
    )
}
